import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { FEATURE_META, type Feature } from '../lib/featureGates';
import {
  buildCheckoutUrl,
  reconcileSubscription,
  annualAvailable,
  ANNUAL_PRICE_USD,
  MONTHLY_PRICE_USD,
  lifetimeAvailable,
  LIFETIME_PRICE_USD,
  FOUNDER_SEATS_LEFT,
  type BillingCycle,
} from '../lib/checkout';
import { useLeakValue } from '../lib/conversionHooks';
import { redeemCoupon, redeemMessage, couponDisclaimer } from '../lib/coupon';

const LockIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

interface Props {
  feature: Feature;
  variant?: 'page' | 'inline';
}

export default function UpgradePrompt({ feature, variant = 'page' }: Props) {
  const { lang, user, reloadFromCloud } = useStore();
  const navigate = useNavigate();
  const isHe = lang === 'he';
  const [verifying, setVerifying] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);
  const [cycle, setCycle] = useState<BillingCycle>(annualAvailable ? 'annual' : 'monthly');
  const [coupon, setCoupon] = useState('');
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponMsg, setCouponMsg] = useState<string | null>(null);
  const [couponOk, setCouponOk] = useState(false);

  const handleRedeem = async () => {
    if (!coupon.trim() || couponBusy) return;
    setCouponBusy(true);
    setCouponMsg(null);
    const res = await redeemCoupon(coupon);
    setCouponMsg(redeemMessage(res.reason, isHe));
    setCouponOk(res.ok);
    if (res.ok) await reloadFromCloud();
    setCouponBusy(false);
  };

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyMsg(null);
    const { activated, reason } = await reconcileSubscription();
    if (activated) {
      setVerifyMsg(isHe ? 'נמצא מנוי פעיל! מרענן…' : 'Active subscription found! Refreshing…');
      await reloadFromCloud();
    } else {
      setVerifyMsg(
        reason === 'membership_bound_elsewhere'
          ? isHe
            ? 'המנוי כבר משויך לחשבון אחר.'
            : 'That membership is linked to another account.'
          : isHe
            ? 'לא נמצא מנוי פעיל למייל של החשבון.'
            : 'No active subscription found for your account email.',
      );
    }
    setVerifying(false);
  };
  const meta = FEATURE_META[feature];
  const name = isHe ? meta.he : meta.en;
  const pitch = isHe ? meta.pitch_he : meta.pitch_en;
  const url = buildCheckoutUrl({ email: user?.email, userId: user?.id, cycle });
  const leak = useLeakValue();
  const leakStr = `$${leak.toLocaleString('en-US')}`;
  const priceLabel =
    cycle === 'annual'
      ? isHe
        ? `$${ANNUAL_PRICE_USD}/שנה`
        : `$${ANNUAL_PRICE_USD}/yr`
      : isHe
        ? `$${MONTHLY_PRICE_USD}/חודש`
        : `$${MONTHLY_PRICE_USD}/mo`;
  const annualSavePct = Math.round((1 - ANNUAL_PRICE_USD / (MONTHLY_PRICE_USD * 12)) * 100);

  if (variant === 'inline') {
    return (
      <div
        style={{
          background: 'var(--primary-soft)',
          border: '1.5px solid var(--bd2)',
          borderRadius: 'var(--rad-l)',
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <div style={{ color: 'var(--primary)', flexShrink: 0 }}>
          <LockIcon />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: 'var(--t1)', marginBottom: 2, fontSize: '.95rem' }}>
            {isHe ? `${name} — תכונת Pro` : `${name} — Pro feature`}
          </div>
          <div style={{ color: 'var(--t2)', fontSize: '.83rem', lineHeight: 1.5 }}>{pitch}</div>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            background: 'var(--grad)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '.83rem',
            border: 'none',
            borderRadius: 'var(--rad-pill)',
            padding: '8px 18px',
            cursor: 'pointer',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {isHe ? 'שדרג ל-Pro' : 'Upgrade to Pro'}
        </a>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
      }}
    >
      <div
        style={{
          background: 'var(--s1)',
          border: '1.5px solid var(--bd)',
          borderRadius: 'var(--rad-l)',
          padding: '48px 40px',
          maxWidth: 440,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 8px 40px -8px rgba(99,91,255,.12)',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'var(--primary-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            color: 'var(--primary)',
          }}
        >
          <LockIcon />
        </div>

        <div
          style={{
            display: 'inline-block',
            background: 'var(--primary-soft)',
            color: 'var(--primary-ink)',
            fontSize: '.72rem',
            fontWeight: 800,
            letterSpacing: '.06em',
            borderRadius: 'var(--rad-pill)',
            padding: '3px 12px',
            marginBottom: 14,
            textTransform: 'uppercase',
          }}
        >
          Pro
        </div>

        <h2
          style={{ color: 'var(--t1)', fontSize: '1.35rem', fontWeight: 800, margin: '0 0 10px' }}
        >
          {name}
        </h2>
        <p
          style={{
            color: 'var(--t2)',
            lineHeight: 1.65,
            margin: leak >= 200 ? '0 0 18px' : '0 0 32px',
            fontSize: '.93rem',
          }}
        >
          {pitch}
        </p>

        {leak >= 200 && (
          <div
            style={{
              background: 'var(--r-bg)',
              color: 'var(--r)',
              borderRadius: 'var(--rad)',
              padding: '10px 14px',
              margin: '0 0 26px',
              fontSize: '.86rem',
              fontWeight: 700,
              lineHeight: 1.5,
            }}
          >
            {isHe
              ? `המאמן זיהה ${leakStr} שדולפים מהמסחר שלך. מנוי אחד עולה פחות מ-1% מזה.`
              : `The Coach found ${leakStr} leaking from your trading. One month of Pro costs a fraction of that.`}
          </div>
        )}

        {annualAvailable && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              background: 'var(--s2)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--rad-pill)',
              padding: 4,
              marginBottom: 18,
            }}
          >
            {(['annual', 'monthly'] as BillingCycle[]).map((c) => {
              const on = cycle === c;
              return (
                <button
                  key={c}
                  onClick={() => setCycle(c)}
                  style={{
                    flex: 1,
                    border: 'none',
                    borderRadius: 'var(--rad-pill)',
                    padding: '8px 10px',
                    fontSize: '.82rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: on ? 'var(--grad)' : 'transparent',
                    color: on ? '#fff' : 'var(--t2)',
                  }}
                >
                  {c === 'annual'
                    ? isHe
                      ? `שנתי · חסוך ${annualSavePct}%`
                      : `Annual · save ${annualSavePct}%`
                    : isHe
                      ? 'חודשי'
                      : 'Monthly'}
                </button>
              );
            })}
          </div>
        )}

        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            background: 'var(--grad)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1rem',
            border: 'none',
            borderRadius: 'var(--rad-pill)',
            padding: '13px 28px',
            cursor: 'pointer',
            textDecoration: 'none',
            marginBottom: 14,
          }}
        >
          {isHe ? `שדרג ל-Pro — ${priceLabel}` : `Upgrade to Pro — ${priceLabel}`}
        </a>

        {lifetimeAvailable && (
          <a
            href={buildCheckoutUrl({ email: user?.email, userId: user?.id, cycle: 'lifetime' })}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              background: 'var(--s2)',
              border: '1.5px solid var(--primary)',
              color: 'var(--primary-ink)',
              fontWeight: 700,
              fontSize: '.88rem',
              borderRadius: 'var(--rad-l)',
              padding: '11px 16px',
              cursor: 'pointer',
              textDecoration: 'none',
              marginBottom: 14,
              lineHeight: 1.4,
            }}
          >
            🔥{' '}
            {isHe
              ? `דיל מייסדים — שלם פעם אחת, שלך לתמיד · $${LIFETIME_PRICE_USD}`
              : `Founder deal — pay once, yours forever · $${LIFETIME_PRICE_USD}`}
            {FOUNDER_SEATS_LEFT > 0 && (
              <span
                style={{ display: 'block', fontSize: '.74rem', color: 'var(--t3)', marginTop: 2 }}
              >
                {isHe
                  ? `נותרו ${FOUNDER_SEATS_LEFT} מקומות במחיר המייסדים`
                  : `Only ${FOUNDER_SEATS_LEFT} founder seats left`}
              </span>
            )}
          </a>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 14,
            flexWrap: 'wrap',
            margin: '0 0 14px',
            fontSize: '.72rem',
            color: 'var(--t3)',
            fontWeight: 600,
          }}
        >
          <span>✓ {isHe ? 'ביטול בכל עת' : 'Cancel anytime'}</span>
          <span>✓ {isHe ? 'פרטי ומאובטח' : 'Private & secure'}</span>
          <span>✓ {isHe ? 'נבנה לסוחרי פרופ' : 'Built for prop traders'}</span>
        </div>

        <button
          onClick={handleVerify}
          disabled={verifying}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--primary)',
            fontSize: '.83rem',
            fontWeight: 600,
            cursor: verifying ? 'default' : 'pointer',
            padding: '4px 8px',
            display: 'block',
            margin: '0 auto 4px',
          }}
        >
          {verifying
            ? isHe
              ? 'בודק…'
              : 'Checking…'
            : isHe
              ? 'כבר שילמת? אמת מנוי'
              : 'Already paid? Verify subscription'}
        </button>
        {verifyMsg && (
          <p style={{ color: 'var(--t2)', fontSize: '.78rem', margin: '0 0 10px' }}>{verifyMsg}</p>
        )}

        {!couponOpen ? (
          <button
            onClick={() => setCouponOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary)',
              fontSize: '.83rem',
              fontWeight: 600,
              cursor: 'pointer',
              padding: '4px 8px',
              display: 'block',
              margin: '0 auto 4px',
            }}
          >
            {isHe ? 'יש לך קוד קופון?' : 'Have a coupon code?'}
          </button>
        ) : (
          <div style={{ margin: '6px 0 4px', textAlign: 'start' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={coupon}
                onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleRedeem()}
                placeholder={isHe ? 'קוד קופון' : 'Coupon code'}
                spellCheck={false}
                dir="ltr"
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '10px 12px',
                  fontSize: '.92rem',
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  textAlign: 'center',
                  border: '1.5px solid var(--bd2)',
                  borderRadius: 'var(--rad)',
                  background: 'var(--s2)',
                  color: 'var(--t1)',
                  outline: 'none',
                }}
              />
              <button
                onClick={handleRedeem}
                disabled={couponBusy || !coupon.trim()}
                style={{
                  background: coupon.trim() ? 'var(--primary)' : 'var(--s3)',
                  color: coupon.trim() ? '#fff' : 'var(--t3)',
                  border: 'none',
                  borderRadius: 'var(--rad)',
                  padding: '10px 16px',
                  fontWeight: 700,
                  fontSize: '.88rem',
                  cursor: couponBusy || !coupon.trim() ? 'default' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {couponBusy ? (isHe ? '…' : '…') : isHe ? 'מימוש' : 'Redeem'}
              </button>
            </div>
            {couponMsg && (
              <p
                style={{
                  color: couponOk ? 'var(--g)' : 'var(--r)',
                  fontSize: '.8rem',
                  fontWeight: 600,
                  margin: '8px 2px 0',
                }}
              >
                {couponMsg}
              </p>
            )}
            <p
              style={{
                color: 'var(--t3)',
                fontSize: '.72rem',
                lineHeight: 1.5,
                margin: '8px 2px 0',
              }}
            >
              {couponDisclaimer(isHe)}
            </p>
          </div>
        )}

        <button
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--t3)',
            fontSize: '.83rem',
            cursor: 'pointer',
            padding: '4px 8px',
            marginTop: 10,
          }}
        >
          {isHe ? '← חזור ל-Dashboard' : '← Back to Dashboard'}
        </button>
      </div>
    </div>
  );
}
